import React from 'react';
import type { SvgIconProps } from '@mui/material';
import {
  CheckCircle,
  Science,
  ModelTraining,
  Error as ErrorIcon,
  Warning,
  Info,
} from '@mui/icons-material';
import { EventStatus, EventFormat } from '../types/events-unified';
import { EVENT_STATUS_CONFIG } from '../constants/event-status.constants';
import { MLModelStatus, MLModelType } from '../types/analytics';

export type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

// ─── Event Status ─────────────────────────────────────────────────────────────

// Цвет чипа статуса — из канона `EVENT_STATUS_CONFIG`
// (`constants/event-status.constants.ts`). Прежде здесь лежала своя копия, и
// она разошлась с каноном в `published`, `ongoing` и `postponed`: одно и то же
// событие получало чипы разного цвета в карточке и в каталоге.
export const getEventStatusColor = (status: EventStatus): ChipColor =>
  EVENT_STATUS_CONFIG[status]?.color ?? 'default';

// ─── Event Format ─────────────────────────────────────────────────────────────

export const EVENT_FORMAT_ICONS: Record<EventFormat, string> = {
  [EventFormat.ONLINE]: '🌐',
  [EventFormat.OFFLINE]: '🏢',
  [EventFormat.HYBRID]: '🔄',
};

export const getEventFormatIcon = (format: EventFormat): string =>
  EVENT_FORMAT_ICONS[format] ?? '';

// ─── ML Model Status ──────────────────────────────────────────────────────────

export const ML_MODEL_STATUS_COLORS: Record<MLModelStatus, ChipColor> = {
  [MLModelStatus.DEPLOYED]: 'success',
  [MLModelStatus.TRAINED]: 'primary',
  [MLModelStatus.TRAINING]: 'warning',
  [MLModelStatus.DEPLOYING]: 'warning',
  [MLModelStatus.ERROR]: 'error',
  [MLModelStatus.DEPRECATED]: 'default',
};

export const getMLModelStatusColor = (status: MLModelStatus): ChipColor =>
  ML_MODEL_STATUS_COLORS[status] ?? 'default';

export const ML_MODEL_STATUS_ICONS: Record<
  MLModelStatus,
  React.ComponentType<SvgIconProps>
> = {
  [MLModelStatus.DEPLOYED]: CheckCircle,
  [MLModelStatus.TRAINED]: Science,
  [MLModelStatus.TRAINING]: ModelTraining,
  [MLModelStatus.DEPLOYING]: Info,
  [MLModelStatus.ERROR]: ErrorIcon,
  [MLModelStatus.DEPRECATED]: Warning,
};

export const getMLModelStatusIcon = (
  status: MLModelStatus,
): React.ComponentType<SvgIconProps> =>
  ML_MODEL_STATUS_ICONS[status] ?? Info;

// ─── ML Model Type ────────────────────────────────────────────────────────────
// TODO: migrate to i18n translation keys

export const ML_MODEL_TYPE_DESCRIPTIONS: Record<MLModelType, string> = {
  [MLModelType.CLASSIFICATION]: 'Классификация данных на категории',
  [MLModelType.REGRESSION]: 'Предсказание числовых значений',
  [MLModelType.CLUSTERING]: 'Группировка похожих данных',
  [MLModelType.ANOMALY_DETECTION]: 'Обнаружение аномалий в данных',
  [MLModelType.TIME_SERIES]: 'Анализ временных рядов',
  [MLModelType.NLP]: 'Обработка естественного языка',
  [MLModelType.RECOMMENDATION]: 'Рекомендательная система',
  [MLModelType.FORECASTING]: 'Прогнозирование будущих значений',
};

export const getMLModelTypeDescription = (type: MLModelType): string =>
  ML_MODEL_TYPE_DESCRIPTIONS[type] ?? 'Машинное обучение';

// ─── Organizer / Generic Status ───────────────────────────────────────────────

export type OrganizerEventStatus =
  | 'planning'
  | 'active'
  | 'completed'
  | 'cancelled'
  | 'confirmed'
  | 'pending'
  | 'declined'
  | 'todo'
  | 'in-progress'
  | 'overdue';

export const ORGANIZER_STATUS_COLORS: Record<OrganizerEventStatus, ChipColor> = {
  planning: 'warning',
  active: 'success',
  completed: 'default',
  cancelled: 'error',
  confirmed: 'success',
  pending: 'warning',
  declined: 'error',
  todo: 'default',
  'in-progress': 'primary',
  overdue: 'error',
};

export const getOrganizerStatusColor = (status: string): ChipColor =>
  (ORGANIZER_STATUS_COLORS as Record<string, ChipColor>)[status] ?? 'default';

// ─── Booking / Order Status ───────────────────────────────────────────────────

export type BookingStatus = 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';

export const BOOKING_STATUS_COLORS: Record<BookingStatus, ChipColor> = {
  pending: 'warning',
  confirmed: 'info',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'error',
};

export const getBookingStatusColor = (status: string): ChipColor =>
  (BOOKING_STATUS_COLORS as Record<string, ChipColor>)[status] ?? 'default';

// ─── Partner Page Status ──────────────────────────────────────────────────────

export type PartnerPageStatus = 'active' | 'pending' | 'inactive';
export type PartnerPagePartnershipType = 'strategic' | 'preferred' | 'standard';

export const PARTNER_STATUS_COLORS: Record<PartnerPageStatus, ChipColor> = {
  active: 'success',
  pending: 'warning',
  inactive: 'default',
};

export const PARTNER_PARTNERSHIP_TYPE_COLORS: Record<PartnerPagePartnershipType, ChipColor> = {
  strategic: 'error',
  preferred: 'warning',
  standard: 'info',
};

export const getPartnerStatusColor = (status: string): ChipColor =>
  (PARTNER_STATUS_COLORS as Record<string, ChipColor>)[status] ?? 'default';

export const getPartnershipTypeColor = (type: string): ChipColor =>
  (PARTNER_PARTNERSHIP_TYPE_COLORS as Record<string, ChipColor>)[type] ?? 'default';
